import { useState, useEffect } from 'react'
import { PageLayout } from '../layout'
import { parseDateString } from '../../utils/dateUtils'
import { fetchRegistrationRequests, inviteRegistrationRequest, rejectRegistrationRequest } from '../../supabase-client'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import SearchInput from '@/components/ui/search-input'
import { toast } from 'sonner'
import { UserPlus, XCircle, CheckCircle, Clock, Send } from 'lucide-react'

const STATUS_BADGES = {
  pending: { label: 'Pending', variant: 'warning' },
  invited: { label: 'Invited', variant: 'info' },
  registered: { label: 'Registered', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
}

export default function RegistrationRequestsList({ user, onLogout }) {
  const [requests, setRequests] = useState([])
  const [filteredRequests, setFilteredRequests] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [customerName, setCustomerName] = useState('')
  const [inviting, setInviting] = useState(false)

  useEffect(() => {
    loadRequests()
  }, [])

  useEffect(() => {
    let filtered = requests
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(r =>
        r.business_name?.toLowerCase().includes(term) ||
        r.contact_name?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term)
      )
    }
    setFilteredRequests(filtered)
  }, [searchTerm, requests])

  const loadRequests = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchRegistrationRequests()
      setRequests(data)
    } catch (err) {
      console.error('Error loading registration requests:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInviteClick = (request) => {
    setSelectedRequest(request)
    setCustomerName(request.business_name)
    setInviteModalOpen(true)
  }

  const handleSendInvite = async () => {
    if (!selectedRequest) return
    setInviting(true)
    try {
      await inviteRegistrationRequest(selectedRequest.id, customerName)
      toast.success(`Invitation sent to ${selectedRequest.email}`)
      setInviteModalOpen(false)
      loadRequests()
    } catch (err) {
      console.error('Error sending invite:', err)
      toast.error('Failed to send invitation: ' + err.message)
    } finally {
      setInviting(false)
    }
  }

  const handleReject = async (request) => {
    if (!confirm(`Reject registration from ${request.business_name}?`)) return
    try {
      await rejectRegistrationRequest(request.id)
      toast.success('Request rejected')
      loadRequests()
    } catch (err) {
      toast.error('Failed to reject: ' + err.message)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    })
  }

  if (loading) {
    return (
      <PageLayout title="Registration Requests" user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <p className="ml-3 text-muted-foreground">Loading...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Registration Requests"
      subtitle="B2B customers requesting portal access"
      user={user}
      onLogout={onLogout}
      headerActions={
        <SearchInput
          placeholder="Search requests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      }
    >
      <div className="h-full overflow-y-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 mb-4 flex items-center justify-between">
            <p className="text-sm">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
          </div>
        )}

        <div className="sales-form-container">
          {filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <UserPlus className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No registration requests</h3>
              <p className="text-muted-foreground text-sm">
                When potential customers register on the portal, their requests will appear here.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => {
                  const statusInfo = STATUS_BADGES[request.status] || STATUS_BADGES.pending
                  return (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.business_name}</TableCell>
                      <TableCell>{request.contact_name}</TableCell>
                      <TableCell className="text-muted-foreground">{request.email}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(request.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {request.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleInviteClick(request)}>
                              <Send className="h-3 w-3 mr-1" />
                              Invite
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleReject(request)}>
                              <XCircle className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {request.status === 'invited' && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Awaiting acceptance
                          </span>
                        )}
                        {request.status === 'registered' && (
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Active
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Portal Invitation</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={selectedRequest?.email || ''} disabled />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="custName">Customer name (for Odin)</Label>
              <Input
                id="custName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Business name in Odin"
              />
            </div>

            {selectedRequest?.message && (
              <div className="space-y-1.5">
                <Label>Their message</Label>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  {selectedRequest.message}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteModalOpen(false)} disabled={inviting}>
              Cancel
            </Button>
            <Button onClick={handleSendInvite} disabled={inviting || !customerName.trim()}>
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )
}
